import { create } from "@/store";
import { immer } from "zustand/middleware/immer";
import { persist, subscribeWithSelector } from "zustand/middleware";
import environment from "@/modules/environment";
import { NoiseSenKeys } from "@/enums/noise";
import { MotionSensKeys } from "@/enums/motion";
import { VideoQualityKeys } from "@/enums/videoQuality";
import { ReplayDurationKeys } from "@/enums/replayDuration";

const initialState: StationState = {
  noiseCorrection: 0,
  role: "unknown",
  icon: "living_room",
  color: "color1",
  objectId: null,
  recordingMode: "ON",
  settings: {
    micSensitivity: NoiseSenKeys.MEDIUM,
    noiseDetectionEnabled: false,
    motionDetectionEnabled: false,
    motionSensitivity: MotionSensKeys.MEDIUM,
    motionDetectionZone: null,
    videoQuality: VideoQualityKeys.HD,
    replayDuration: ReplayDurationKeys["8h"]
  },
  supportedFeatures: []
};

const useStation = create<StationStore>()(
  subscribeWithSelector(
    persist(
      immer((set) => ({
        ...initialState,
        setRole(role) {
          log.info("setting station role to: ", role);
          set({ role });
        },
        setNoiseCorrection(noiseCorrection) {
          log.info("setting noiseCorrection to ", noiseCorrection);
          set((state) => {
            state.noiseCorrection = noiseCorrection;
          });
        },
        setIcon(icon) {
          log.info("setting station icon to: ", icon);
          set((state) => {
            state.icon = icon;
          });
        },
        setColor(color) {
          log.info("setting station color to: ", color);
          set((state) => {
            state.color = color;
          });
        },
        setObjectId(objectId) {
          set((state) => {
            state.objectId = objectId;
          });
        },
        updateSettings(update) {
          log.info("Update for station settings", update);
          const { motionDetectionZone, ...rest } = update;
          set((state) => {
            Object.assign(state.settings, rest);

            if (!("motionDetectionZone" in update)) return;
            const nextZoneMask = motionDetectionZone?.gridMask ?? null;
            const prevZoneMask = state.settings.motionDetectionZone?.gridMask ?? null;
            if (nextZoneMask?.bits.toString() !== prevZoneMask?.bits.toString()) {
              state.settings.motionDetectionZone = motionDetectionZone || null;
            }
          });
        },
        setRecordingMode(recordingMode) {
          log.info("setting recording mode to: ", recordingMode);
          set({ recordingMode });
        },
        setSupportedFeatures(supportedFeatures) {
          log.info("setting supported features to: ", supportedFeatures);
          set({ supportedFeatures });
        },
        resetState() {
          log.info("resetting station store");
          set(initialState);
        }
      })),
      {
        name: environment.appMode + "-station"
      }
    )
  )
);

const stationStore = useStation.getState;

export { useStation, stationStore };
